export type OfacImportRelationship =
  | 'customer'
  | 'vendor'
  | 'payee'
  | 'ubo_director'
  | 'employee'
  | 'contractor'
  | 'intermediary'
  | 'vessel'
  | 'aircraft';

export type OfacImportEntityType = 'person' | 'company' | 'vessel' | 'aircraft';

export type OfacImportRow = {
  lineNumber: number;
  entityName: string;
  relationshipType: OfacImportRelationship;
  entityType: OfacImportEntityType;
  identifier?: string;
  birthdate?: string;
  country?: string;
  hireLocation?: string;
  notes?: string;
  screenNow: boolean;
};

export type OfacImportParseResult = {
  rows: OfacImportRow[];
  errors: string[];
};

export const OFAC_SCREENING_CSV_HEADERS = [
  'name',
  'entity_type',
  'relationship',
  'identifier',
  'birthdate',
  'country',
  'hire_location',
  'notes',
  'screen_now',
] as const;

export const OFAC_SCREENING_CSV_TEMPLATE = `${OFAC_SCREENING_CSV_HEADERS.join(',')}
Acme Trading LLC,company,customer,12-3456789,,,,Primary vendor for AP,false
Jane Q Public,person,ubo_director,,1978-04-28,,,Beneficial owner,false
MV Ocean Star,vessel,vessel,IMO9123456,,,,Flagged charter candidate,false
Alex Rivera,person,contractor,ID-998877,1995-01-20,,,Hire roster example,false
`;

const RELATIONSHIPS = new Set<OfacImportRelationship>([
  'customer',
  'vendor',
  'payee',
  'ubo_director',
  'employee',
  'contractor',
  'intermediary',
  'vessel',
  'aircraft',
]);

const ENTITY_TYPES = new Set<OfacImportEntityType>(['person', 'company', 'vessel', 'aircraft']);

const parseCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
};

const normalizeHeader = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '_');

const inferEntityType = (
  relationship: OfacImportRelationship,
  explicit?: string,
): OfacImportEntityType => {
  const normalized = String(explicit || '').trim().toLowerCase() as OfacImportEntityType;
  if (normalized && ENTITY_TYPES.has(normalized)) {
    return normalized;
  }
  if (relationship === 'vessel' || relationship === 'aircraft') {
    return relationship;
  }
  if (relationship === 'ubo_director' || relationship === 'employee' || relationship === 'contractor') {
    return 'person';
  }
  return 'company';
};

const parseBoolean = (value: string | undefined, fallback: boolean): boolean => {
  if (value == null || !String(value).trim()) {
    return fallback;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'n'].includes(normalized)) {
    return false;
  }
  return fallback;
};

const normalizeBirthdate = (value: string | undefined): string | undefined => {
  const raw = String(value || '').trim();
  if (!raw) {
    return undefined;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const month = slash[1].padStart(2, '0');
    const day = slash[2].padStart(2, '0');
    return `${slash[3]}-${month}-${day}`;
  }
  return raw;
};

export const parseOfacScreeningCsv = (
  text: string,
  options?: { defaultScreenNow?: boolean },
): OfacImportParseResult => {
  const defaultScreenNow = Boolean(options?.defaultScreenNow);
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return { rows: [], errors: ['File is empty.'] };
  }

  const headerCells = parseCsvLine(lines[0]).map(normalizeHeader);
  const indexOf = (name: string) => headerCells.indexOf(name);
  const nameIdx = indexOf('name');
  if (nameIdx < 0) {
    return {
      rows: [],
      errors: ['Missing required column: name. Download the CSV template and keep the header row unchanged.'],
    };
  }

  const relationshipIdx = indexOf('relationship');
  const entityTypeIdx = indexOf('entity_type');
  const identifierIdx = indexOf('identifier');
  const birthdateIdx = indexOf('birthdate');
  const countryIdx = indexOf('country');
  const hireLocationIdx = indexOf('hire_location');
  const notesIdx = indexOf('notes');
  const screenNowIdx = indexOf('screen_now');

  const rows: OfacImportRow[] = [];
  const errors: string[] = [];

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex += 1) {
    const lineNumber = lineIndex + 1;
    const cells = parseCsvLine(lines[lineIndex]);
    const entityName = String(cells[nameIdx] || '').trim();
    if (!entityName) {
      errors.push(`Line ${lineNumber}: name is required.`);
      continue;
    }

    const relationshipRaw = relationshipIdx >= 0
      ? String(cells[relationshipIdx] || '').trim().toLowerCase()
      : 'customer';
    const relationshipType = (relationshipRaw || 'customer') as OfacImportRelationship;
    if (!RELATIONSHIPS.has(relationshipType)) {
      errors.push(
        `Line ${lineNumber}: invalid relationship "${relationshipRaw}". Use: ${[...RELATIONSHIPS].join(', ')}.`,
      );
      continue;
    }

    const entityType = inferEntityType(
      relationshipType,
      entityTypeIdx >= 0 ? cells[entityTypeIdx] : undefined,
    );
    const birthdate = normalizeBirthdate(birthdateIdx >= 0 ? cells[birthdateIdx] : undefined);
    if (birthdate && !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      errors.push(`Line ${lineNumber}: birthdate must be YYYY-MM-DD (or MM/DD/YYYY).`);
      continue;
    }

    rows.push({
      lineNumber,
      entityName,
      relationshipType,
      entityType,
      identifier: identifierIdx >= 0 ? String(cells[identifierIdx] || '').trim() || undefined : undefined,
      birthdate,
      country: countryIdx >= 0 ? String(cells[countryIdx] || '').trim() || undefined : undefined,
      hireLocation: hireLocationIdx >= 0 ? String(cells[hireLocationIdx] || '').trim() || undefined : undefined,
      notes: notesIdx >= 0 ? String(cells[notesIdx] || '').trim() || undefined : undefined,
      screenNow: parseBoolean(screenNowIdx >= 0 ? cells[screenNowIdx] : undefined, defaultScreenNow),
    });
  }

  return { rows, errors };
};

export const downloadOfacScreeningCsvTemplate = (): void => {
  const blob = new Blob([OFAC_SCREENING_CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'nextcompli-ofac-screening-template.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
